import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homePrimaryContentItemContent$key } from "~/queries/__generated__/homePrimaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Content,
  CoverImage,
  Headline,
  LinkHighlightArea,
  Section,
  StyledEntityLink,
  Summary,
} from "./styles";

export type Props = {
  reference: homePrimaryContentItemContent$key;
  variantProps?: ContentItemHomePrimaryVariantProps;
} & ContentItemRenderVariantProps;

export type ContentItemHomePrimaryVariantProps = {
  withComment?: boolean;
  withLiveTag?: boolean;
};

export const ContentItemHomePrimary = forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      onClick,
      provider,
      reference: reference_,
      variantProps = {
        withComment: false,
        withLiveTag: false,
      },
    },
    reference,
  ) => {
    const content = useFragment(
      graphql`
        fragment homePrimaryContentItemContent on Content {
          ...entityLink
          entityId
        }
      `,
      reference_,
    );

    return (
      <Container className={className} ref={reference}>
        <Content>
          <Section>{provider.sections()}</Section>

          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            <LinkHighlightArea>
              <Headline>
                {provider.headline({ hideTooltip: true, preferSocialHeadline: true })}
                {variantProps?.withLiveTag && provider.liveTag()}
              </Headline>
              <Summary>
                {provider.summary({
                  isTextOverflowEllipsis: true,
                })}
              </Summary>
            </LinkHighlightArea>
          </StyledEntityLink>
          <ActionBar>
            {provider.actionBar({
              commentVariant: "reverse",
              isBookmarkVisible: false,
              isCommentsVisible: variantProps?.withComment ?? false,
            })}
          </ActionBar>
        </Content>
        <CoverImage>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            {provider.coverImage({
              responsiveVariants: {
                desktopUp: "size1200x800",
                mobileUp: "size540x360",
                tabletUp: "size1200x800",
              },
              withCaption: true,
            })}
          </StyledEntityLink>
        </CoverImage>
      </Container>
    );
  },
);

ContentItemHomePrimary.displayName = "ContentItemHomePrimary";
